"use client";

import { useState, useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RejoignezLeMouvement() {
  const [isEnglish, setIsEnglish] = useState(false);
  useEffect(() => {
    const checkLanguage = () => {
      const lang = localStorage.getItem("lacolonne_language") || "fr";
      setIsEnglish(lang === "en");
    };
    checkLanguage();
    window.addEventListener("storage", checkLanguage);
    window.addEventListener("languagechange", checkLanguage);
    return () => {
      window.removeEventListener("storage", checkLanguage);
      window.removeEventListener("languagechange", checkLanguage);
    };
  }, []);
  const {
    ref: eyebrowRef,
    isVisible: eyebrowVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.1
  });
  const {
    ref: subRef,
    isVisible: subVisible
  } = useRevealOnScroll<HTMLParagraphElement>();
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: numeralRef,
    isVisible: numeralVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: dividerRef,
    isVisible: dividerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const words = isEnglish ? ["Building", "our", "future"] : ["Bâtissons", "notre", "avenir"];
  return <section data-section-id="5128" id="rejoignez-le-mouvement" className="bg-background text-foreground relative overflow-hidden py-32 md:py-48 lg:py-[200px] px-4 sm:px-6 lg:px-8" style={{
    backgroundColor: "var(--background-obsidian)"
  }}>
      <div className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto grid grid-cols-12 gap-4">
        <div ref={eyebrowRef} className={`col-span-12 md:col-start-2 md:col-span-10 mb-12 md:mb-16 transition-all duration-700 ease-out ${eyebrowVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase" style={{
          color: "var(--text-muted)"
        }}>
            {isEnglish ? "Get Involved · 06" : "Engagement · 06"}
          </span>
        </div>

        <div className="col-span-12 md:col-start-2 md:col-span-10 relative">
          <div ref={numeralRef} className={`pointer-events-none absolute -top-16 md:-top-28 right-0 md:-right-8 select-none transition-all duration-1000 ease-out ${numeralVisible ? "opacity-100" : "opacity-0"}`} aria-hidden="true">
            <span className="font-serif italic font-light leading-none text-[200px] md:text-[360px] lg:text-[460px]" style={{
            color: "var(--background-canvas)",
            opacity: 0.1
          }}>
              C
            </span>
          </div>

          <h2 ref={headlineRef} className="relative font-default font-bold leading-[0.9] tracking-tighter text-[56px] sm:text-[80px] md:text-[120px] lg:text-[144px] xl:text-[160px]" style={{
          color: "var(--background-canvas)"
        }}>
            <span className="block overflow-hidden">
              {words.map((word, i) => <span data-index={i} key={`${word}-${i}`} className="inline-block italic font-serif mr-[0.2em] transition-all ease-out" style={{
              transitionDuration: "800ms",
              transitionDelay: `${i * 100}ms`,
              transform: headlineVisible ? "translateY(0)" : "translateY(100%)",
              opacity: headlineVisible ? 1 : 0
            }}>
                  {word}
                </span>)}
            </span>
            <span className="block overflow-hidden">
              <span className="inline-block not-italic font-bold transition-all ease-out" style={{
              transitionDuration: "800ms",
              transitionDelay: "300ms",
              transform: headlineVisible ? "translateY(0)" : "translateY(100%)",
              opacity: headlineVisible ? 1 : 0
            }}>
                {isEnglish ? "together" : "ensemble"}
              </span>
              <span className="inline-block italic font-serif transition-all ease-out" style={{
              transitionDuration: "800ms",
              transitionDelay: "400ms",
              transform: headlineVisible ? "translateY(0)" : "translateY(100%)",
              opacity: headlineVisible ? 1 : 0
            }}>
                .
              </span>
            </span>
          </h2>
        </div>

        <p ref={subRef} className={`col-span-12 md:col-start-4 md:col-span-8 mt-10 md:mt-14 font-default text-base md:text-lg lg:text-[20px] leading-relaxed max-w-2xl transition-all duration-700 ease-out delay-500 ${subVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        color: "var(--background-canvas)",
        opacity: 0.8
      }}>
          {isEnglish ? "Support the integration and well-being of francophone seniors and families in Edmonton." : "Soutenez l'intégration et le bien-être des aînés et des familles francophones à Edmonton."}
        </p>

        <div ref={ctaRef} className={`col-span-12 md:col-start-4 md:col-span-9 mt-12 md:mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 transition-all duration-700 ease-out delay-700 ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Button asChild size="lg" className="h-auto rounded-2xl px-8 py-4 sm:px-10 sm:py-5 text-sm sm:text-base font-bold uppercase tracking-wider shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 bg-[var(--secondary)] text-[var(--secondary-foreground)] hover:bg-[var(--secondary)]/90">
            <Link to="/BecomeMemberPage">
              {isEnglish ? "Become member" : "Devenir membre"}
              <ArrowRight className="ml-2 size-5" />
            </Link>
          </Button>

          <Button asChild variant="outline" size="lg" className="h-auto rounded-2xl px-8 py-4 sm:px-10 sm:py-5 text-sm sm:text-base font-bold uppercase tracking-wider border-2 border-[var(--secondary)] text-[var(--secondary)] hover:bg-[var(--secondary)] hover:text-[var(--secondary-foreground)] bg-transparent transition-all duration-300 hover:-translate-y-0.5">
            <Link to="/OurProgramsPage">
              {isEnglish ? "Our programs" : "Nos programmes"}
            </Link>
          </Button>
        </div>

        <div ref={dividerRef} className={`col-span-12 md:col-start-2 md:col-span-10 mt-24 md:mt-32 flex items-center gap-6 transition-all duration-1000 ease-out delay-300 ${dividerVisible ? "opacity-100" : "opacity-0"}`}>
          <div className="h-px flex-1" style={{
          backgroundColor: "var(--background-canvas)",
          opacity: 0.2
        }} />
          <span className="font-serif text-lg tracking-[0.3em]" style={{
          color: "var(--background-canvas)",
          opacity: 0.4
        }} aria-hidden="true">
            &lowast; &lowast; &lowast;
          </span>
          <div className="h-px flex-1" style={{
          backgroundColor: "var(--background-canvas)",
          opacity: 0.2
        }} />
        </div>
      </div>
    </section>;
}